// 参数模板分组布局 (2026-10-23, 与后端 schemautil/layout.go 同一口径):
// 模板除字段清单 fields 外显式存一份 页签 → 分组 结构 layout; 字段用 tab / sec 挂到其中.
//   - 页签 / 分组有哪些、先后顺序, 只取 layout (可以有暂时没字段的空分组);
//   - 组内字段顺序 = fields 里的先后;
//   - layout 为空 = 未启用分组 (存量模板/参数行), 字段照旧平铺.
// 不按字段上的 tab/sec 名字现拼分组.

export interface FieldGroupAttrs {
    /** 所属页签名 (须在 layout 里存在) */
    tab?: string;
    /** 所属分组名 (须在该页签的 sections 里存在) */
    sec?: string;
}

export interface LayoutSection {
    sec: string;
}

export interface LayoutTab {
    tab: string;
    sections: LayoutSection[];
}

/** 未启用分组 */
export type Layout = LayoutTab[] | null;

export interface FieldSection<T> {
    sec: string;
    items: T[];
}

export interface FieldTab<T> {
    tab: string;
    sections: FieldSection<T>[];
}

/** 字段的 tab/sec 在布局里找不到时 (存量脏数据 / 模板外字段) 展示用的兜底页签名, 不落库. */
export const UNPLACED_TAB = '未归组';

/** 解析接口返回的 layout JSON 串; 空 / null / [] = 未启用. */
export function parseLayout(raw: string | null | undefined): Layout {
    if (!raw) return null;
    try {
        const v = JSON.parse(raw);
        if (!Array.isArray(v) || v.length === 0) return null;
        return (v as LayoutTab[]).map((t) => ({
            tab: String(t.tab ?? ''),
            sections: (t.sections || []).map((s) => ({ sec: String(s.sec ?? '') })),
        }));
    } catch {
        return null;
    }
}

/** 序列化给接口; 未启用传 null (后端落 NULL). */
export function stringifyLayout(layout: Layout): string | null {
    return layout && layout.length > 0 ? JSON.stringify(layout) : null;
}

const norm = (v?: string) => (v || '').trim();

/** 分组的唯一键 (页签 + 分组名; 用不会出现在名称里的分隔符). */
export function sectionKey(tab: string, sec: string): string {
    return `${tab}\u001f${sec}`;
}

/** 字段是否落在布局里. */
export function fitsLayout(f: FieldGroupAttrs, layout: Layout): boolean {
    if (!layout) return true;
    const t = layout.find((x) => x.tab === norm(f.tab));
    return !!t && t.sections.some((s) => s.sec === norm(f.sec));
}

/**
 * 按布局组装 页签 → 分组 → 项: 结构完全取 layout (含空分组), 项按自身 tab/sec 挂上去.
 * 无布局 = 单页签单分组 (名称为空, 渲染时即平铺). 挂不上的项收进末尾「未归组」, 只为可见, 不落库.
 */
export function groupFields<T>(items: T[], attrs: (item: T) => FieldGroupAttrs, layout: Layout): FieldTab<T>[] {
    if (!layout) return items.length ? [{ tab: '', sections: [{ sec: '', items }] }] : [];
    const tabs: FieldTab<T>[] = layout.map((t) => ({
        tab: t.tab,
        sections: t.sections.map((s) => ({ sec: s.sec, items: [] as T[] })),
    }));
    const idx = new Map<string, FieldSection<T>>();
    tabs.forEach((t) => t.sections.forEach((s) => idx.set(sectionKey(t.tab, s.sec), s)));
    const unplaced: T[] = [];
    for (const it of items) {
        const a = attrs(it);
        const s = idx.get(sectionKey(norm(a.tab), norm(a.sec)));
        if (s) s.items.push(it);
        else unplaced.push(it);
    }
    if (unplaced.length) tabs.push({ tab: UNPLACED_TAB, sections: [{ sec: '', items: unplaced }] });
    return tabs;
}

function swap<X>(arr: X[], i: number, j: number): X[] {
    if (i < 0 || j < 0 || i >= arr.length || j >= arr.length) return arr;
    const next = [...arr];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
}

/** 页签整体左移 / 右移 (只动布局, 字段不用动). */
export function moveTab(layout: LayoutTab[], tab: string, dir: -1 | 1): LayoutTab[] {
    const i = layout.findIndex((t) => t.tab === tab);
    return swap(layout, i, i + dir);
}

/** 页签内某分组上移 / 下移. */
export function moveSection(layout: LayoutTab[], tab: string, sec: string, dir: -1 | 1): LayoutTab[] {
    return layout.map((t) => {
        if (t.tab !== tab) return t;
        const i = t.sections.findIndex((s) => s.sec === sec);
        return { ...t, sections: swap(t.sections, i, i + dir) };
    });
}

/**
 * 保存参数行前对齐布局: 有布局但部分字段挂不上 (模板外字段) 时, 在布局末尾补一个同名页签/分组收纳它们,
 * 保证后端「字段必须落在布局里」的校验通过且这些字段的值不丢.
 */
export function placeIntoLayout<T extends FieldGroupAttrs>(fields: T[], layout: Layout): { fields: T[]; layout: Layout } {
    if (!layout) return { fields, layout };
    if (fields.every((f) => fitsLayout(f, layout))) return { fields, layout };
    const next = [...layout.filter((t) => t.tab !== UNPLACED_TAB), { tab: UNPLACED_TAB, sections: [{ sec: UNPLACED_TAB }] }];
    return {
        fields: fields.map((f) => (fitsLayout(f, layout) ? f : { ...f, tab: UNPLACED_TAB, sec: UNPLACED_TAB })),
        layout: next,
    };
}
